
import React, { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from '../layouts/ErrorFallback';
import { trackError } from '@/utils/error/errorTracker';

interface ErrorBoundaryClassProps {
  children: ReactNode;
  showToast: (message: string) => void;
  onError: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Core error boundary class component
 * Handles error catching and displays fallback UI
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorState> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error: Error): ErrorState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Update state with error info
    this.setState({ errorInfo });
    
    // Call parent handler
    this.props.onError(error, errorInfo);
    
    // Show toast notification
    this.props.showToast(`Error detected: ${error.message}`);
    
    // Log error to console for debugging
    console.error('🔴 Global error boundary caught an error:', error);
    console.error('Component stack trace:', errorInfo.componentStack);

    // Track the error with CRITICAL severity for admin notification
    trackError(error, 'GlobalErrorBoundary', {
      componentStack: errorInfo.componentStack,
      isCritical: true
    });

    // Try to log to remote monitoring if available
    try {
      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      // Store locally for recovery purposes
      localStorage.setItem('last_error', JSON.stringify(errorData));
    } catch (loggingError) {
      console.error('Failed to log error details:', loggingError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error || new Error('Unknown error')}
          errorInfo={this.state.errorInfo || undefined}
          critical={true}
          componentName="GlobalErrorBoundary"
          resetErrorBoundary={() => {
            this.setState({ hasError: false, error: null, errorInfo: null });
          }}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryClass;
